// ============================================================================
// CREATE SESSION TASK MODAL
// Multi-step wizard for creating session-scoped async multi-agent tasks
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Circle,
  Clock,
  BarChart3,
  GitBranch,
  FileText,
  Loader,
  ClipboardCheck,
  ImageIcon,
  Check
} from 'lucide-react';
import { getDeviceImages, DeviceImageMetadata } from '../../services/deviceImages';
import {
  SessionTaskTemplate,
  CreateSessionTaskInput,
  SessionTaskContext
} from '../../types/session-task.types';
import sessionTasksService from '../../services/sessionTasksService';

interface CreateSessionTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  projectId?: string;
  sessionContext: SessionTaskContext;
  designContext?: string;
  onSuccess: (workflowId: string, executionId: string) => void;
}

type WizardStep = 'template' | 'customize' | 'context' | 'review';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'template', label: '1. Template' },
  { id: 'customize', label: '2. Customize' },
  { id: 'context', label: '3. Context' },
  { id: 'review', label: '4. Review' },
];

const STEP_SUBTITLES: Record<WizardStep, string> = {
  template: 'Select a Task Template',
  customize: 'Customize Task Details',
  context: 'Select Context for the Task',
  review: 'Review and Launch Task',
};

const getTemplateIcon = (templateId: string) => {
  if (templateId.includes('bcaf') || templateId.includes('assessment')) return <ClipboardCheck size={24} />;
  if (templateId.includes('diagram') || templateId.includes('architecture')) return <GitBranch size={24} />;
  if (templateId.includes('story') || templateId.includes('synthesis')) return <FileText size={24} />;
  return <BarChart3 size={24} />;
};

// Generic multi-select toggle helper — pure function of its arguments, shared
// across the "Context" step's transcript/requirement/persona/avatar pickers.
const toggleSelection = (id: string, selectedIds: string[], setSelectedIds: (ids: string[]) => void) => {
  if (selectedIds.includes(id)) {
    setSelectedIds(selectedIds.filter(sid => sid !== id));
  } else {
    setSelectedIds([...selectedIds, id]);
  }
};

// ----------------------------------------------------------------------------
// Submit-time helper: builds the `customContext` payload depending on which
// template is selected (HTML artifact vs. plain custom-context text).
// ----------------------------------------------------------------------------
interface BuildTaskCustomContextParams {
  isHtmlArtifactTemplate: boolean;
  instruction: string;
  selectedImageIds: string[];
  deviceImages: DeviceImageMetadata[];
  designContext?: string;
  customContext: string;
}

const buildTaskCustomContext = ({
  isHtmlArtifactTemplate,
  instruction,
  selectedImageIds,
  deviceImages,
  designContext,
  customContext
}: BuildTaskCustomContextParams): Record<string, any> | undefined => {
  if (isHtmlArtifactTemplate) {
    const imageS3Keys = selectedImageIds
      .map(id => deviceImages.find(img => img.imageId === id)?.s3Key)
      .filter((k): k is string => !!k);
    return {
      text: instruction.trim(),
      ...(imageS3Keys.length > 0 ? { imageS3Keys } : {}),
      ...(designContext ? { designContext } : {}),
    };
  }
  if (customContext.trim()) {
    return { text: customContext.trim() };
  }
  return undefined;
};

// ----------------------------------------------------------------------------
// Step 1: Template selection
// ----------------------------------------------------------------------------
interface TemplateStepProps {
  loadingTemplates: boolean;
  templates: SessionTaskTemplate[];
  selectedTemplate: SessionTaskTemplate | null;
  onSelectTemplate: (template: SessionTaskTemplate) => void;
}

const TemplateStep: React.FC<TemplateStepProps> = ({
  loadingTemplates,
  templates,
  selectedTemplate,
  onSelectTemplate
}) => (
  <div>
    {loadingTemplates ? (
      <div className="text-center py-10 text-slate-500">
        <Loader size={24} className="animate-spin mx-auto mb-3" />
        Loading templates...
      </div>
    ) : (
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-8"></th>
            <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Template</th>
            <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
            <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Duration</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(template => {
            const isSelected = selectedTemplate?.templateId === template.templateId;
            return (
              <tr
                key={template.templateId}
                onClick={() => onSelectTemplate(template)}
                className={`cursor-pointer border-b border-slate-100 transition-colors ${
                  isSelected ? 'bg-primary/5' : 'hover:bg-slate-50'
                }`}
              >
                <td className="py-3 pr-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-primary/20 text-primary' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {getTemplateIcon(template.templateId)}
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-slate-900'}`}>
                      {template.name}
                    </span>
                    {isSelected && <CheckCircle size={14} className="text-primary shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 m-0 line-clamp-1">
                    {template.description}
                  </p>
                </td>
                <td className="py-3 pr-4">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${
                    template.capabilities.sessionTaskCategory === 'thinking'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {template.capabilities.sessionTaskCategory}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                    <Clock size={12} />
                    {template.capabilities.estimatedDuration}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

// ----------------------------------------------------------------------------
// Step 2: Customize (task name, template details, HTML-artifact instruction
// + reference images, optional schedule)
// ----------------------------------------------------------------------------
interface CustomizeStepProps {
  selectedTemplate: SessionTaskTemplate;
  taskName: string;
  onTaskNameChange: (value: string) => void;
  isHtmlArtifactTemplate: boolean;
  instruction: string;
  onInstructionChange: (value: string) => void;
  deviceImages: DeviceImageMetadata[];
  loadingImages: boolean;
  selectedImageIds: string[];
  onSelectedImageIdsChange: (ids: string[]) => void;
  enableSchedule: boolean;
  onEnableScheduleChange: (value: boolean) => void;
  scheduleInterval: number;
  onScheduleIntervalChange: (value: number) => void;
  scheduleMaxExecutions: number | undefined;
  onScheduleMaxExecutionsChange: (value: number | undefined) => void;
}

const CustomizeStep: React.FC<CustomizeStepProps> = ({
  selectedTemplate,
  taskName,
  onTaskNameChange,
  isHtmlArtifactTemplate,
  instruction,
  onInstructionChange,
  deviceImages,
  loadingImages,
  selectedImageIds,
  onSelectedImageIdsChange,
  enableSchedule,
  onEnableScheduleChange,
  scheduleInterval,
  onScheduleIntervalChange,
  scheduleMaxExecutions,
  onScheduleMaxExecutionsChange
}) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Task Name
      </label>
      <input
        type="text"
        value={taskName}
        onChange={(e) => onTaskNameChange(e.target.value)}
        placeholder="Enter a descriptive name for this task"
        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Template Details
      </label>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
        <p className="text-sm text-slate-900 m-0">
          <strong>Category:</strong> {selectedTemplate.capabilities.sessionTaskCategory}
        </p>
        <p className="text-sm text-slate-900 m-0">
          <strong>Duration:</strong> {selectedTemplate.capabilities.estimatedDuration}
        </p>
        {selectedTemplate.metadata.dependencies.avatars && selectedTemplate.metadata.dependencies.avatars.length > 0 && (
          <p className="text-sm text-slate-900 m-0">
            <strong>Avatars:</strong> {selectedTemplate.metadata.dependencies.avatars.join(', ')}
          </p>
        )}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Usage Notes
      </label>
      <ul className="m-0 pl-5 text-sm text-slate-500 leading-relaxed space-y-1">
        {selectedTemplate.metadata.usageNotes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </div>

    {/* HTML Artifact-specific: Instruction + Images */}
    {isHtmlArtifactTemplate && (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Instruction <span className="text-red-500">*</span>
          </label>
          <textarea
            value={instruction}
            onChange={(e) => onInstructionChange(e.target.value)}
            placeholder="Describe the HTML page you want, e.g. 'Build a dark-themed Mermaid process flow presentation showing the DA claim journey with 3 slides and key stats on each slide'"
            rows={5}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-[inherit] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
          <p className="text-xs text-slate-400 mt-1.5">
            Be specific about layout, charts, colour scheme, and data. The AI will generate a self-contained HTML file.
          </p>
        </div>

        {/* Reference Images */}
        {(deviceImages.length > 0 || loadingImages) && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <ImageIcon size={14} className="inline mr-1.5 -mt-0.5" />
              Reference Images <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Select whiteboard or device images to include as visual context. The AI will use them when building the HTML.
            </p>
            {loadingImages ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader size={12} className="animate-spin" /> Loading device images...
              </div>
            ) : (
              <>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {deviceImages.map(img => {
                    const isSelected = selectedImageIds.includes(img.imageId);
                    return (
                      <button
                        key={img.imageId}
                        type="button"
                        onClick={() => onSelectedImageIdsChange(
                          isSelected ? selectedImageIds.filter(id => id !== img.imageId) : [...selectedImageIds, img.imageId]
                        )}
                        className={`relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        title={img.extractedText ? `${img.captureLabel || 'Image'}: ${img.extractedText.substring(0, 100)}...` : img.captureLabel || 'No text extracted yet'}
                      >
                        <img
                          src={img.presignedUrl}
                          alt={img.captureLabel || 'Device image'}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                        {img.extractionStatus === 'complete' && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">
                            {img.captureLabel || 'Ready'}
                          </div>
                        )}
                        {img.extractionStatus === 'pending' && (
                          <div className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-[9px] px-1 py-0.5 text-center">
                            Processing...
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedImageIds.length > 0 && (
                  <p className="text-xs text-primary mt-1">
                    {selectedImageIds.length} image{selectedImageIds.length > 1 ? 's' : ''} selected as reference
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    )}

    {/* Schedule (optional) */}
    <div>
      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700">
        <input
          type="checkbox"
          checked={enableSchedule}
          onChange={(e) => onEnableScheduleChange(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/30"
        />
        Run on a schedule
      </label>

      {enableSchedule && (
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Repeat every
            </label>
            <select
              value={scheduleInterval}
              onChange={(e) => onScheduleIntervalChange(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Max executions (optional)
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={scheduleMaxExecutions ?? ''}
              onChange={(e) => onScheduleMaxExecutionsChange(e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Unlimited"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
        </div>
      )}
    </div>
  </div>
);

// ----------------------------------------------------------------------------
// Step 3: Context selection (transcript entries, requirements, personas,
// avatars, free-text custom context)
// ----------------------------------------------------------------------------
interface ContextStepProps {
  sessionContext: SessionTaskContext;
  selectedTranscriptIds: string[];
  setSelectedTranscriptIds: (ids: string[]) => void;
  selectedRequirementIds: string[];
  setSelectedRequirementIds: (ids: string[]) => void;
  selectedPersonaIds: string[];
  setSelectedPersonaIds: (ids: string[]) => void;
  selectedAvatarIds: string[];
  setSelectedAvatarIds: (ids: string[]) => void;
  customContext: string;
  onCustomContextChange: (value: string) => void;
}

const ContextStep: React.FC<ContextStepProps> = ({
  sessionContext,
  selectedTranscriptIds,
  setSelectedTranscriptIds,
  selectedRequirementIds,
  setSelectedRequirementIds,
  selectedPersonaIds,
  setSelectedPersonaIds,
  selectedAvatarIds,
  setSelectedAvatarIds,
  customContext,
  onCustomContextChange
}) => (
  <div className="space-y-6">
    {/* Transcript Entries */}
    {sessionContext.transcriptEntries.length > 0 && (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Transcript Entries ({selectedTranscriptIds.length} selected)
        </label>
        <div className="max-h-[200px] overflow-y-auto border border-slate-200 rounded-lg">
          {sessionContext.transcriptEntries.map(entry => (
            <div
              key={entry.id}
              onClick={() => toggleSelection(entry.id, selectedTranscriptIds, setSelectedTranscriptIds)}
              className={`p-3 border-b border-slate-50 cursor-pointer flex gap-3 items-start transition-colors ${
                selectedTranscriptIds.includes(entry.id) ? 'bg-primary/5' : 'hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedTranscriptIds.includes(entry.id)}
                onChange={() => {}}
                className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary/30"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-400 mb-1">
                  {entry.role} &bull; {new Date(entry.timestamp).toLocaleString()}
                </div>
                <div className="text-sm text-slate-700 truncate">
                  {entry.preview}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Requirements */}
    {sessionContext.requirements.length > 0 && (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Requirements ({selectedRequirementIds.length} selected)
        </label>
        <div className="max-h-[200px] overflow-y-auto border border-slate-200 rounded-lg">
          {sessionContext.requirements.map(req => (
            <div
              key={req.id}
              onClick={() => toggleSelection(req.id, selectedRequirementIds, setSelectedRequirementIds)}
              className={`p-3 border-b border-slate-50 cursor-pointer flex gap-3 items-start transition-colors ${
                selectedRequirementIds.includes(req.id) ? 'bg-primary/5' : 'hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedRequirementIds.includes(req.id)}
                onChange={() => {}}
                className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary/30"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 mb-1">
                  {req.title}
                </div>
                <div className="text-[13px] text-slate-500 truncate">
                  {req.description.substring(0, 100)}{req.description.length > 100 ? '...' : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Personas */}
    {sessionContext.personas.length > 0 && (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Personas ({selectedPersonaIds.length} selected)
        </label>
        <div className="flex flex-wrap gap-2">
          {sessionContext.personas.map(persona => (
            <div
              key={persona.id}
              onClick={() => toggleSelection(persona.id, selectedPersonaIds, setSelectedPersonaIds)}
              className={`px-3 py-2 rounded-lg cursor-pointer text-sm transition-all ${
                selectedPersonaIds.includes(persona.id)
                  ? 'border-2 border-primary bg-primary/5 text-primary font-medium'
                  : 'border border-slate-200 hover:border-primary/50 hover:bg-primary/5'
              }`}
            >
              {persona.name} ({persona.role})
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Avatars */}
    {sessionContext.sessionAvatars.length > 0 && (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Avatars ({selectedAvatarIds.length} selected)
        </label>
        <div className="flex flex-wrap gap-2">
          {sessionContext.sessionAvatars.map(avatar => (
            <div
              key={avatar.id}
              onClick={() => toggleSelection(avatar.id, selectedAvatarIds, setSelectedAvatarIds)}
              className={`px-3 py-2 rounded-lg cursor-pointer text-sm transition-all ${
                selectedAvatarIds.includes(avatar.id)
                  ? 'border-2 border-primary bg-primary/5 text-primary font-medium'
                  : 'border border-slate-200 hover:border-primary/50 hover:bg-primary/5'
              }`}
            >
              {avatar.name} ({avatar.role})
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Custom Context */}
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Custom Context (Optional)
      </label>
      <textarea
        value={customContext}
        onChange={(e) => onCustomContextChange(e.target.value)}
        placeholder="Add any additional context, instructions, or focus areas for the task..."
        rows={4}
        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-[inherit] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
      />
    </div>
  </div>
);

// ----------------------------------------------------------------------------
// Step 4: Review + launch
// ----------------------------------------------------------------------------
interface ReviewStepProps {
  selectedTemplate: SessionTaskTemplate;
  taskName: string;
  selectedTranscriptIds: string[];
  selectedRequirementIds: string[];
  selectedPersonaIds: string[];
  selectedAvatarIds: string[];
  isHtmlArtifactTemplate: boolean;
  instruction: string;
  selectedImageIds: string[];
  deviceImages: DeviceImageMetadata[];
  customContext: string;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  selectedTemplate,
  taskName,
  selectedTranscriptIds,
  selectedRequirementIds,
  selectedPersonaIds,
  selectedAvatarIds,
  isHtmlArtifactTemplate,
  instruction,
  selectedImageIds,
  deviceImages,
  customContext
}) => (
  <div className="space-y-5">
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
      <h3 className="m-0 mb-4 text-base font-semibold text-slate-900">
        Task Summary
      </h3>
      <div className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Template:</span>
          <span className="text-sm text-slate-900">{selectedTemplate.name}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Task Name:</span>
          <span className="text-sm text-slate-900">{taskName}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Category:</span>
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${
            selectedTemplate.capabilities.sessionTaskCategory === 'thinking'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-amber-100 text-amber-700'
          }`}>
            {selectedTemplate.capabilities.sessionTaskCategory}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Est. Duration:</span>
          <span className="text-sm text-slate-900">{selectedTemplate.capabilities.estimatedDuration}</span>
        </div>
      </div>
    </div>

    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
      <h3 className="m-0 mb-4 text-base font-semibold text-slate-900">
        Selected Context
      </h3>
      <div className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Transcript Entries:</span>
          <span className="text-sm text-slate-900">{selectedTranscriptIds.length}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Requirements:</span>
          <span className="text-sm text-slate-900">{selectedRequirementIds.length}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Personas:</span>
          <span className="text-sm text-slate-900">{selectedPersonaIds.length}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Avatars:</span>
          <span className="text-sm text-slate-900">{selectedAvatarIds.length}</span>
        </div>
        {isHtmlArtifactTemplate && (
          <div>
            <span className="text-sm font-medium text-slate-500 block mb-1">Instruction:</span>
            <div className="text-sm text-slate-900 p-2 bg-white rounded-lg whitespace-pre-wrap">
              {instruction || <em className="text-slate-400">No instruction provided</em>}
            </div>
            {selectedImageIds.length > 0 && (
              <div className="mt-2">
                <span className="text-sm font-medium text-slate-500 block mb-1">Reference Images:</span>
                <div className="flex gap-1.5">
                  {selectedImageIds.map(id => {
                    const img = deviceImages.find(i => i.imageId === id);
                    return img ? (
                      <img
                        key={id}
                        src={img.presignedUrl}
                        alt={img.captureLabel || 'Reference'}
                        className="w-12 h-12 rounded object-cover border border-slate-200"
                      />
                    ) : null;
                  })}
                  <span className="text-xs text-slate-500 self-center ml-1">
                    {selectedImageIds.length} image{selectedImageIds.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        {!isHtmlArtifactTemplate && customContext && (
          <div>
            <span className="text-sm font-medium text-slate-500 block mb-1">Custom Context:</span>
            <div className="text-sm text-slate-900 p-2 bg-white rounded-lg whitespace-pre-wrap">
              {customContext}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const CreateSessionTaskModal: React.FC<CreateSessionTaskModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  projectId,
  sessionContext,
  designContext,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('template');
  const [templates, setTemplates] = useState<SessionTaskTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTaskTemplate | null>(null);
  const [taskName, setTaskName] = useState('');
  const [customContext, setCustomContext] = useState('');
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<string[]>([]);
  const [selectedRequirementIds, setSelectedRequirementIds] = useState<string[]>([]);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [selectedAvatarIds, setSelectedAvatarIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // HTML Artifact-specific state
  const [instruction, setInstruction] = useState('');

  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);
  const [deviceImages, setDeviceImages] = useState<DeviceImageMetadata[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Schedule state
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState(10);
  const [scheduleMaxExecutions, setScheduleMaxExecutions] = useState<number | undefined>(undefined);

  const isHtmlArtifactTemplate = selectedTemplate?.templateId === 'session-task-html-artifact';

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen && templates.length === 0) {
      loadTemplates();
    }
  }, [isOpen, templates.length]);

  // Load device images when HTML artifact template is selected
  useEffect(() => {
    if (!isHtmlArtifactTemplate) return;
    let cancelled = false;
    setLoadingImages(true);
    getDeviceImages(sessionId)
      .then(images => { if (!cancelled) setDeviceImages(images); })
      .catch(() => { if (!cancelled) setDeviceImages([]); })
      .finally(() => { if (!cancelled) setLoadingImages(false); });
    return () => { cancelled = true; };
  }, [isHtmlArtifactTemplate, sessionId]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const loadedTemplates = await sessionTasksService.getTaskTemplates();
      setTemplates(loadedTemplates);
    } catch (err: any) {
      setError('Failed to load task templates: ' + err.message);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (template: SessionTaskTemplate) => {
    setSelectedTemplate(template);
    setTaskName(template.name);
    setCurrentStep('customize');
  };

  const handleNext = () => {
    if (currentStep === 'template' && selectedTemplate) {
      setCurrentStep('customize');
    } else if (currentStep === 'customize') {
      setCurrentStep('context');
    } else if (currentStep === 'context') {
      setCurrentStep('review');
    }
  };

  const handleBack = () => {
    if (currentStep === 'customize') {
      setCurrentStep('template');
    } else if (currentStep === 'context') {
      setCurrentStep('customize');
    } else if (currentStep === 'review') {
      setCurrentStep('context');
    }
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const taskCustomContext = buildTaskCustomContext({
        isHtmlArtifactTemplate,
        instruction,
        selectedImageIds,
        deviceImages,
        designContext,
        customContext
      });

      const input: CreateSessionTaskInput = {
        taskTemplateId: selectedTemplate.templateId,
        taskName: taskName.trim(),
        taskCategory: selectedTemplate.capabilities.sessionTaskCategory,
        contextSnapshot: {
          transcriptEntryIds: selectedTranscriptIds,
          requirementIds: selectedRequirementIds,
          personaIds: selectedPersonaIds,
          avatarIds: selectedAvatarIds,
          customContext: taskCustomContext
        },
        ...(enableSchedule ? {
          scheduleConfig: {
            intervalMinutes: scheduleInterval,
            ...(scheduleMaxExecutions ? { maxExecutions: scheduleMaxExecutions } : {})
          }
        } : {})
      };

      const result = await sessionTasksService.createTask(sessionId, input);
      onSuccess(result.workflowId, result.executionId);
      handleClose();
    } catch (err: any) {
      setError('Failed to create task: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCurrentStep('template');
      setSelectedTemplate(null);
      setTaskName('');
      setCustomContext('');
      setInstruction('');
      setSelectedTranscriptIds([]);
      setSelectedRequirementIds([]);
      setSelectedPersonaIds([]);
      setSelectedAvatarIds([]);
      setSelectedImageIds([]);
      setEnableSchedule(false);
      setScheduleInterval(10);
      setScheduleMaxExecutions(undefined);
      setError(null);
      onClose();
    }
  };

  const isNextDisabled =
    !selectedTemplate ||
    (currentStep === 'customize' && !taskName.trim()) ||
    (currentStep === 'customize' && isHtmlArtifactTemplate && !instruction.trim()) ||
    isSubmitting;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center"
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl max-w-[900px] w-[90%] max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-8 pt-8 pb-4 flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <h1 className="text-slate-900 text-2xl font-bold leading-tight m-0">
                Create Session Task
              </h1>
              <p className="text-slate-500 text-sm font-normal m-0">
                Step {currentStepIndex + 1}: {STEP_SUBTITLES[currentStep]}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500 disabled:cursor-not-allowed"
            >
              <X size={20} />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-8 py-4 flex gap-3 border-b border-slate-100">
            {STEPS.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = index < currentStepIndex;

              let stepClass: string;
              if (isActive) stepClass = 'bg-primary text-white font-semibold shadow-sm';
              else if (isCompleted) stepClass = 'bg-emerald-500 text-white font-semibold';
              else stepClass = 'bg-slate-100 text-slate-500 font-medium';

              return (
                <div
                  key={step.id}
                  className={`flex h-9 items-center justify-center gap-x-2 rounded-full px-4 text-sm transition-colors ${stepClass}`}
                >
                  {isCompleted ? <CheckCircle size={16} /> : <Circle size={16} />} {/* NOSONAR */}
                  <span>{step.label}</span>
                </div>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-8 overflow-y-auto flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Template Selection */}
            {currentStep === 'template' && (
              <TemplateStep
                loadingTemplates={loadingTemplates}
                templates={templates}
                selectedTemplate={selectedTemplate}
                onSelectTemplate={handleTemplateSelect}
              />
            )}

            {/* Step 2: Customize */}
            {currentStep === 'customize' && selectedTemplate && (
              <CustomizeStep
                selectedTemplate={selectedTemplate}
                taskName={taskName}
                onTaskNameChange={setTaskName}
                isHtmlArtifactTemplate={isHtmlArtifactTemplate}
                instruction={instruction}
                onInstructionChange={setInstruction}
                deviceImages={deviceImages}
                loadingImages={loadingImages}
                selectedImageIds={selectedImageIds}
                onSelectedImageIdsChange={setSelectedImageIds}
                enableSchedule={enableSchedule}
                onEnableScheduleChange={setEnableSchedule}
                scheduleInterval={scheduleInterval}
                onScheduleIntervalChange={setScheduleInterval}
                scheduleMaxExecutions={scheduleMaxExecutions}
                onScheduleMaxExecutionsChange={setScheduleMaxExecutions}
              />
            )}

            {/* Step 3: Context Selection */}
            {currentStep === 'context' && (
              <ContextStep
                sessionContext={sessionContext}
                selectedTranscriptIds={selectedTranscriptIds}
                setSelectedTranscriptIds={setSelectedTranscriptIds}
                selectedRequirementIds={selectedRequirementIds}
                setSelectedRequirementIds={setSelectedRequirementIds}
                selectedPersonaIds={selectedPersonaIds}
                setSelectedPersonaIds={setSelectedPersonaIds}
                selectedAvatarIds={selectedAvatarIds}
                setSelectedAvatarIds={setSelectedAvatarIds}
                customContext={customContext}
                onCustomContextChange={setCustomContext}
              />
            )}

            {/* Step 4: Review */}
            {currentStep === 'review' && selectedTemplate && (
              <ReviewStep
                selectedTemplate={selectedTemplate}
                taskName={taskName}
                selectedTranscriptIds={selectedTranscriptIds}
                selectedRequirementIds={selectedRequirementIds}
                selectedPersonaIds={selectedPersonaIds}
                selectedAvatarIds={selectedAvatarIds}
                isHtmlArtifactTemplate={isHtmlArtifactTemplate}
                instruction={instruction}
                selectedImageIds={selectedImageIds}
                deviceImages={deviceImages}
                customContext={customContext}
              />
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 'template' || isSubmitting}
              className={`flex items-center justify-center gap-2 px-5 h-11 rounded-lg border text-sm font-semibold transition-all ${
                currentStep === 'template' || isSubmitting
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <ChevronLeft size={16} />
              Back
            </button>

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex items-center justify-center px-6 h-11 rounded-lg border border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {currentStep !== 'review' ? (
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled}
                  className="flex items-center justify-center gap-2 px-8 h-11 rounded-lg bg-primary text-white font-bold text-sm shadow-lg shadow-primary/20 transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center justify-center gap-2 px-8 h-11 rounded-lg bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  {isSubmitting ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Launching...
                    </>
                  ) : (
                    'Launch Task'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
